import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "../../site/ui";
import { fmtDate } from "../../site/pages/Forum";

/* =========================================================
   운영자 빌드 검수 (/laonabot/admin/builds) — 라오나봇 관리자만
   서버가 만든 spec.md 를 사무실 빌드 워커가 가져가 코드를 생성 · 빌드하고 산출물을 올리면(review),
   운영자가 여기서 로그 · 산출물을 보고 승인(done)하거나 다시 빌드시킨다. 승인하면 회원이 서명 링크로 받는다.
   (백엔드 app/laona_worker_api.py · 상태 머신 app/laona_orders.py)
   ========================================================= */

const TABS = [
  ["review", "검수 대기"], ["failed", "실패"], ["pending", "대기"], ["building", "빌드 중"],
  ["done", "완료"], ["cancelled", "취소"], ["", "전체"],
];
const LABEL = Object.fromEntries(TABS.filter(([k]) => k));
/* 상태 배지 색 — 기존 lb-tag--st 톤을 쓰고, 실패만 이 화면 클래스로 */
const TONE = { review: "lb-tag--wait", building: "lb-tag--on", done: "lb-tag--done", failed: "lb-ab-tag--fail" };
const SOURCE_MAX = 20 << 20;

async function adminApi(path, options = {}) {
  const res = await fetch(`/api/admin${path}`, {
    credentials: "same-origin",
    headers: typeof options.body === "string" ? { "Content-Type": "application/json" } : undefined,
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data.detail;
    const err = new Error(typeof d === "string" ? d
      : Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : "요청을 처리하지 못했습니다.");
    err.status = res.status;
    throw err;
  }
  return data;
}

const orderPath = (id) => `/orders/${encodeURIComponent(id)}`;
const fileUrl = (id, name) => `/api/admin${orderPath(id)}/files/${encodeURIComponent(name)}`;

/* 지난 시간 — 분 단위 숫자 또는 ISO 시각에서 */
function minutesText(m) {
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  if (m < 60 * 48) return `${Math.floor(m / 60)}시간 전`;
  return `${Math.floor(m / 1440)}일 전`;
}
function ago(iso) {
  if (!iso) return "";
  const t = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  return Number.isNaN(t) ? "" : minutesText(Math.max(0, Math.floor((Date.now() - t) / 60000)));
}
const fmtSize = (n) => (n >= 1 << 20 ? `${(n / 1048576).toFixed(1)}MB` : n >= 1024 ? `${Math.round(n / 1024)}KB` : `${n || 0}B`);

function BuildTag({ status }) {
  return <span className={`lb-tag lb-tag--st ${TONE[status] || ""}`}>{LABEL[status] || status}</span>;
}

/* 워커 신호 — 15분 넘게 없으면 '끊김' */
function Workers({ items }) {
  if (items === null) return null;
  if (!items.length) {
    return <div className="lb-ab-workers"><span className="lb-ab-worker lb-ab-worker--stale"><b>워커 신호 없음</b></span></div>;
  }
  return (
    <ul className="lb-ab-workers" aria-label="빌드 워커">
      {items.map((w) => (
        <li key={w.worker_id} className={`lb-ab-worker${w.stale ? " lb-ab-worker--stale" : ""}`}
            title={w.last_seen ? `마지막 신호 ${fmtDate(w.last_seen, true)}` : undefined}>
          <b>{w.worker_id}</b>
          <span>{w.stale && "끊김 · "}마지막 신호 {minutesText(w.minutes)}</span>
          {w.version && <span>v{w.version}</span>}
          {w.queue_len != null && <span>대기열 {w.queue_len}</span>}
        </li>
      ))}
    </ul>
  );
}

/* 펼친 줄의 상세 — spec.md · 빌드 로그 · 산출물 · 전이 기록 · 처리 버튼 */
function OrderDetail({ id, rowStatus, onChanged }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const noteId = useId();
  const fileId = useId();

  const load = useCallback(
    () => adminApi(orderPath(id)).then(setD).catch((e) => setError(e.message)),
    [id],
  );
  /* 목록이 30초마다 갱신돼 줄의 상태가 바뀌면(워커가 올림 · 다른 운영자가 처리) 펼친 상세도 다시 읽는다 — 메모 · 고른 파일은 둔다 */
  useEffect(() => { load(); }, [load, rowStatus]);

  async function act(key, confirmText, fn, doneText) {
    if (!window.confirm(confirmText)) return;
    setBusy(key);
    setError("");
    try {
      setD(await fn());
      setNote("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onChanged(doneText);
    } catch (e) {
      setError(e.message);
      load(); // 소스만 올라가고 재빌드가 막힌 경우 등 — 지금 상태로 다시 보인다
    } finally {
      setBusy("");
    }
  }

  const setStatus = (to) => adminApi(`${orderPath(id)}/status`, { method: "POST", body: JSON.stringify({ to, note }) });

  function rebuild() {
    if (file && file.size > SOURCE_MAX) { setError("소스 ZIP 은 20MB 까지입니다."); return; }
    act("rebuild",
      file ? `수정 소스 '${file.name}' 을(를) 올리고 주문 ${id} 를 다시 빌드할까요?\n지금 산출물은 지워집니다.`
        : `주문 ${id} 를 다시 빌드할까요?\n지금 산출물은 지워지고 워커가 대기열에서 다시 가져갑니다.`,
      async () => {
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          await adminApi(`${orderPath(id)}/source`, { method: "POST", body: fd });
        }
        return setStatus("pending");
      },
      `주문 ${id} — 다시 빌드하도록 대기열에 넣었습니다.`);
  }

  if (!d) {
    return <div className="lb-ab-detail">{error ? <p className="lb-error">{error}</p> : <p className="lb-ab-note">불러오는 중…</p>}</div>;
  }
  const st = d.status;
  const canFix = st === "review" || st === "failed";
  const arts = Object.entries(d.artifacts || {});

  return (
    <div className="lb-ab-detail">
      <dl className="lb-ab-facts">
        <div><dt>보드</dt><dd>{d.board || "-"}{d.flash?.chipFamily ? ` · ${d.flash.chipFamily}` : ""}</dd></div>
        <div><dt>카탈로그</dt><dd>{d.catalog_version || "-"}</dd></div>
        <div><dt>빌드 비용</dt><dd>${Number(d.cost_usd || 0).toFixed(2)}</dd></div>
        <div><dt>빌드 끝</dt><dd>{d.built_at ? fmtDate(d.built_at, true) : "-"}</dd></div>
        <div><dt>검수</dt><dd>{d.reviewed_at ? fmtDate(d.reviewed_at, true) : "-"}</dd></div>
      </dl>

      <section className="lb-ab-sec">
        <h4>기능 정의서 (spec.md)</h4>
        {d.spec_md ? (
          <details className="lb-ab-fold">
            <summary>spec.md 펼치기 · {d.spec_md.split("\n").length}줄</summary>
            <pre className="lb-ab-pre">{d.spec_md}</pre>
          </details>
        ) : <p className="lb-ab-note">spec.md 가 없습니다.</p>}
        <div className="lb-ab-btns">
          {d.spec_md && <a className="lb-btn lb-btn--sm" href={fileUrl(id, "spec.md")}>spec.md 받기</a>}
          {d.has_source_zip && <a className="lb-btn lb-btn--sm" href={fileUrl(id, "source.zip")}>수정 소스 ZIP 받기</a>}
        </div>
      </section>

      <section className="lb-ab-sec">
        <h4>빌드 로그 (끝부분)</h4>
        {d.build_log_tail ? <pre className="lb-ab-pre">{d.build_log_tail}</pre> : <p className="lb-ab-note">아직 로그가 없습니다.</p>}
      </section>

      <section className="lb-ab-sec">
        <h4>산출물</h4>
        {arts.length ? (
          <div className="lb-ab-scroll">
            <table className="lb-ab-table">
              <thead><tr><th>파일</th><th>크기</th><th>sha256</th><th aria-label="받기" /></tr></thead>
              <tbody>
                {arts.map(([name, a]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{fmtSize(a.size)}</td>
                    <td title={a.sha256}><code>{(a.sha256 || "").slice(0, 12)}</code></td>
                    <td><a className="lb-btn lb-btn--sm" href={fileUrl(id, name)}>받기</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="lb-ab-note">올라온 산출물이 없습니다.</p>}
      </section>

      <section className="lb-ab-sec">
        <h4>전이 기록</h4>
        {d.events?.length ? (
          <ol className="lb-ab-timeline">
            {d.events.map((e, i) => (
              <li key={i}>
                <div>
                  <time dateTime={e.at}>{fmtDate(e.at, true)}</time>
                  <b>{e.from ? LABEL[e.from] || e.from : "새 주문"} → {LABEL[e.to] || e.to}</b>
                  <span>{e.actor}</span>
                </div>
                {e.note && <p>{e.note}</p>}
              </li>
            ))}
          </ol>
        ) : <p className="lb-ab-note">기록이 없습니다.</p>}
      </section>

      {d.reviewer_note && (
        <section className="lb-ab-sec">
          <h4>운영자 메모</h4>
          <p className="lb-ab-memo">{d.reviewer_note}</p>
        </section>
      )}

      {(canFix || d.has_source_zip) && (
        <section className="lb-ab-sec lb-ab-acts">
          <h4>처리</h4>
          {canFix && (
            <>
              <label htmlFor={noteId}>메모 (선택 — 전이 기록 · 운영자 메모에 남습니다)</label>
              <textarea id={noteId} className="lb-ab-input" rows={2} maxLength={2000} value={note}
                        onChange={(e) => setNote(e.target.value)} />
              <label htmlFor={fileId}>수정 소스 ZIP (선택 — 고르면 먼저 올린 뒤 재빌드합니다. 20MB 까지)</label>
              <input id={fileId} ref={fileRef} type="file" accept=".zip,application/zip"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </>
          )}
          <div className="lb-ab-btns">
            {st === "review" && (
              <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" disabled={!!busy}
                      onClick={() => act("done", `주문 ${id} 를 승인할까요?\n회원에게 완성 메일이 가고, 프로젝트 화면에서 펌웨어를 받을 수 있게 됩니다.`,
                        () => setStatus("done"), `주문 ${id} — 승인했습니다.`)}>
                {busy === "done" ? "승인하는 중…" : "승인 (done)"}
              </button>
            )}
            {canFix && (
              <button type="button" className="lb-btn lb-btn--sm" disabled={!!busy} onClick={rebuild}>
                {busy === "rebuild" ? "넣는 중…" : file ? "수정 소스로 재빌드" : "재빌드"}
              </button>
            )}
            {d.has_source_zip && (
              <button type="button" className="lb-btn lb-btn--sm" disabled={!!busy || !canFix}
                      title={canFix ? undefined : "검수 대기 · 실패 상태에서만 뺄 수 있습니다"}
                      onClick={() => act("unsource", `주문 ${id} 의 수정 소스를 뺄까요?\n다음 빌드에서는 워커가 코드를 다시 생성합니다.`,
                        () => adminApi(`${orderPath(id)}/source`, { method: "DELETE" }), `주문 ${id} — 수정 소스를 뺐습니다.`)}>
                {busy === "unsource" ? "빼는 중…" : "수정 소스 빼기"}
              </button>
            )}
            {st === "failed" && (
              <button type="button" className="lb-btn lb-btn--sm lb-btn--danger" disabled={!!busy}
                      onClick={() => act("cancel", `주문 ${id} 를 취소할까요?\n회원 화면에는 '빌드가 취소되었습니다'로 보입니다.`,
                        () => setStatus("cancelled"), `주문 ${id} — 취소했습니다.`)}>
                {busy === "cancel" ? "취소하는 중…" : "취소"}
              </button>
            )}
          </div>
        </section>
      )}
      {error && <p className="lb-error">{error}</p>}
    </div>
  );
}

function OrderRow({ row, open, onToggle, onChanged }) {
  const p = row.project;
  return (
    <li className={`lb-ab-row${open ? " is-open" : ""}`}>
      <div className="lb-ab-row__top">
        <button type="button" className="lb-ab-row__no" aria-expanded={open} onClick={onToggle}>
          <span aria-hidden="true">{open ? "▾" : "▸"}</span>{row.id}
        </button>
        <BuildTag status={row.status} />
        {row.has_source_zip && <span className="lb-tag">수정 소스</span>}
        {p ? (
          <Link to={`/laonabot/projects/${p.id}`} className="lb-ab-row__title">{p.title} <span>#{p.id}</span></Link>
        ) : <span className="lb-ab-row__title">프로젝트 없음</span>}
      </div>
      <div className="lb-ab-row__meta">
        {row.member && <span>{row.member.name} · {row.member.email}</span>}
        <span>보드 {row.board || "-"}</span>
        <span title={fmtDate(row.created_at, true)}>생성 {fmtDate(row.created_at, true)} · {ago(row.created_at)}</span>
        {row.status === "building" && row.claimed_at && <span>빌드 시작 {ago(row.claimed_at)}</span>}
        {row.status === "review" && row.built_at && <span>빌드 끝 {ago(row.built_at)}</span>}
        <span>재시도 {row.retry_count}회</span>
        {row.worker_id && <span>워커 {row.worker_id}</span>}
      </div>
      {row.error && !open && <p className="lb-ab-row__err">{row.error}</p>}
      {open && <OrderDetail id={row.id} rowStatus={row.status} onChanged={onChanged} />}
    </li>
  );
}

/* spec 검증 실패 — 원인(카탈로그 · 명세)을 고친 뒤 그 프로젝트의 빌드 주문을 다시 만든다 (/api/laona/projects/{id}/build) */
function SpecRetry({ pid, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function retry() {
    if (!window.confirm(`프로젝트 #${pid} 의 spec.md 를 다시 만들어 대기열에 넣을까요?`)) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`/api/laona/projects/${pid}/build`, { method: "POST", credentials: "same-origin" });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "다시 요청하지 못했습니다.");
      onChanged(d.build?.build_status === "pending" ? `프로젝트 #${pid} — 대기열에 넣었습니다` : `프로젝트 #${pid} — 아직 spec 검증에 실패합니다`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="lb-ab-row__meta">
      <button type="button" className="lb-btn lb-btn--sm" disabled={busy} onClick={retry}>{busy ? "다시 만드는 중…" : "spec 다시 만들어 대기열에 넣기"}</button>
      {error && <span className="lb-error">{error}</span>}
    </div>
  );
}

export default function LaonaAdminBuilds() {
  const [tab, setTab] = useState("review");
  const [list, setList] = useState({ status: "loading", items: [], counts: {}, spec_failed: [] });
  const [workers, setWorkers] = useState(null);
  const [open, setOpen] = useState("");
  const [notice, setNotice] = useState("");
  const seq = useRef(0);

  useSeo({ title: "빌드 검수 — LaonaBot", path: "/laonabot/admin/builds", noindex: true });

  /* quiet — 주기 갱신 · 처리 뒤 갱신은 목록을 비우지 않고, 잠깐의 오류는 넘긴다 */
  const load = useCallback(async (quiet) => {
    const n = ++seq.current;
    if (!quiet) setList((s) => ({ ...s, status: "loading", items: [] }));
    try {
      const [d, w] = await Promise.all([adminApi(`/orders${tab ? `?status=${tab}` : ""}`), adminApi("/workers")]);
      if (n !== seq.current) return;
      setList({ status: "ready", items: d.items || [], counts: d.counts || {}, spec_failed: d.spec_failed || [] });
      setWorkers(w.items || []);
    } catch (e) {
      if (n !== seq.current) return;
      const denied = e.status === 401 || e.status === 403;
      if (quiet && !denied) return;
      setList((s) => ({ ...s, status: denied ? "denied" : "error", error: e.message }));
    }
  }, [tab]);

  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onChanged = useCallback((text) => { setNotice(text || ""); load(true); }, [load]);

  if (list.status === "denied") {
    return (
      <section className="lb-section lb-section--top">
        <div className="lb-wrap">
          <div className="lb-empty lb-empty--box">
            <b>라오나봇 관리자만 볼 수 있습니다</b>
            <p>빌드 검수는 라오나봇 관리자 계정으로 로그인해야 열립니다.</p>
            <div className="lb-actions lb-actions--center">
              <Link to="/laonabot/projects" className="lb-btn">내 프로젝트로</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const counts = list.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <section className="lb-section lb-section--top">
      <div className="lb-wrap">
        <header className="lb-head lb-head--row">
          <div>
            <p className="lb-kicker">ADMIN · BUILDS</p>
            <h1>빌드 검수</h1>
            <p className="lb-lead">사무실 빌드 워커가 만든 펌웨어를 확인하고 승인합니다. 승인한 펌웨어만 회원이 내려받을 수 있습니다.</p>
          </div>
          <div className="lb-head__tools">
            <button type="button" className="lb-btn" onClick={() => { setNotice(""); load(false); }}>새로고침</button>
          </div>
        </header>

        <Workers items={workers} />

        {list.spec_failed?.length > 0 && (
          <div className="lb-ab-alert" role="alert">
            <b>spec 검증 실패 — 대기열에 들어가지 못한 주문 {list.spec_failed.length}건</b>
            <ul>
              {list.spec_failed.map((x) => (
                <li key={x.id}>
                  <div className="lb-ab-row__meta">
                    <span className="lb-ab-mono">{x.id}</span>
                    <Link to={`/laonabot/projects/${x.project_id}`}>프로젝트 #{x.project_id}</Link>
                    <span>{fmtDate(x.created_at, true)}</span>
                  </div>
                  {x.note && <pre className="lb-ab-pre">{x.note}</pre>}
                  <SpecRetry pid={x.project_id} onChanged={onChanged} />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="lb-ab-tabs" role="group" aria-label="빌드 상태">
          {TABS.map(([k, label]) => (
            <button key={k || "all"} type="button" className={`lb-ab-tab${tab === k ? " is-active" : ""}`} aria-pressed={tab === k}
                    onClick={() => { setTab(k); setOpen(""); setNotice(""); }}>
              {label} <span className="lb-ab-tab__n">{k ? counts[k] || 0 : total}</span>
            </button>
          ))}
        </div>

        {notice && <p className="lb-ab-ok" role="status">{notice}</p>}

        {list.status === "loading" ? <p className="lb-empty">불러오는 중입니다…</p>
          : list.status === "error" ? <p className="lb-error">{list.error || "주문을 불러오지 못했습니다."}</p>
          : !list.items.length ? <p className="lb-empty">{tab ? `${LABEL[tab]} 주문이 없습니다.` : "빌드 주문이 없습니다."}</p>
          : (
            <ul className="lb-ab-list">
              {list.items.map((row) => (
                <OrderRow key={row.id} row={row} open={open === row.id} onChanged={onChanged}
                          onToggle={() => setOpen((v) => (v === row.id ? "" : row.id))} />
              ))}
            </ul>
          )}
      </div>
    </section>
  );
}
